import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import { CircularProgress, Divider, Stack, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import Button from "../../components/buttons/Button";
import CardWithIcon from "../../components/cards/CardWithIcon";
import CodeInput from "../../components/inputs/CodeInput";
import { useApi } from "../../contexts/ApiContext";
import { useAppContext } from "../../contexts/AppContext";
import {
  MFARequestRequest,
  MFAVerifyRequest,
  useAuth,
} from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import Translations from "../../lang/en/pages.json";
import Minimal from "../../layouts/Minimal";

const StyledIcon = styled(VpnKeyOutlinedIcon)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  padding: 8,
  borderRadius: 8,
  color: "white",
  boxSizing: "content-box",
}));

const MFA_RENSEND_SECONDS = 60;

const MultiFactor: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(MFA_RENSEND_SECONDS);

  const { isLoading, setIsLoading } = useAppContext();
  const { showError, showSuccess } = useError();
  const { client: apiClient } = useApi();
  const navigate = useNavigate();
  const { user, setMfaRequired } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);

  console.info("MultiFactor component mounted");

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      console.info("submitting...");
      if (await verifyMFACode(verificationCode)) {
        showSuccess(Translations.pages.auth_page.mfa.success);
        setMfaRequired(false);
        navigate("/welcome", { replace: true });
      } else {
        console.error("Verification failed");
        showError(Translations.pages.auth_page.errors.invalid_code);
      }
    } catch (error) {
      console.error("Verification error:", error);
      showError(Translations.pages.auth_page.errors.invalid_code);
      setVerificationCode("");
    } finally {
      setVerificationCode("");

      setTimeout(() => {
        setIsSubmitting(false);
      }, 400);
    }
  };

  const sendCode = useCallback(async (force = false) => {
    let interval: any = null;
    console.info("Sending MFA code...");

    try {
      const reqData: MFARequestRequest = { type: "phone", force: force };
      const response = await apiClient.POST("/v1/mfa/request", {
        body: reqData,
      });

      if (!response.response.ok) {
        throw new Error("Failed to send MFA code");
      }

      showSuccess(
        `Verification code sent to your ${
          response.data?.masked_email
            ? `email: ${response.data.masked_email}`
            : "phone"
        }.`
      );
    } catch (error) {
      console.error("Error sending MFA code:", error);

      if (force) {
        showError(Translations.pages.auth_page.errors.error_sending_code);
      }
    } finally {
      setSecondsLeft(MFA_RENSEND_SECONDS);
      interval = setInterval(() => {
        setSecondsLeft((prev) => {
          if (prev <= 0) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return interval;
  }, []);

  const verifyMFACode = async (code: string): Promise<boolean> => {
    const reqData: MFAVerifyRequest = {
      mfa_code: code,
    };
    const response = await apiClient.POST("/v1/mfa/verify", {
      body: reqData,
    });
    return !!response.data?.verified;
  };

  useEffect(() => {
    if (verificationCode.length === 6) {
      handleSubmit();
    }
  }, [verificationCode]);

  useEffect(() => {
    const interval: any = sendCode();

    return () => {
      clearInterval(interval);
      setSecondsLeft(MFA_RENSEND_SECONDS);
    };
  }, []);

  const canResend = useMemo(() => {
    return secondsLeft <= 0;
  }, [secondsLeft]);

  const submitTitle = useMemo(() => {
    if (isSubmitting) {
      return <CircularProgress size={20} />;
    }
    const title = canResend
      ? Translations.pages.auth_page.mfa.buttons.resend
      : Translations.pages.auth_page.mfa.buttons.resend_disabled.replace(
          "{seconds}",
          secondsLeft.toString()
        );
    return <Typography variant="button">{title}</Typography>;
  }, [canResend, isSubmitting, secondsLeft]);

  return (
    <Minimal>
      <Stack mb={4} spacing={2} textAlign="center">
        <Typography color="#266A4A" variant="h5" fontWeight="bold">
          {Translations.pages.auth_page.mfa.title}
        </Typography>
        <Typography variant="body2">
          {Translations.pages.auth_page.mfa.description}
        </Typography>
      </Stack>
      <CardWithIcon
        withButton={false}
        width={600}
        icon={<StyledIcon />}
        buttonText={Translations.pages.auth_page.mfa.buttons.verify}
        disabled={isSubmitting}
      >
        <Stack spacing={8} textAlign="center">
          <Stack spacing={2}>
            <Typography variant="subtitle1">
              {Translations.pages.auth_page.mfa.code.helper_text}
            </Typography>
          </Stack>

          <Divider orientation="horizontal" />

          <Stack spacing={4}>
            <CodeInput
              digits={6}
              onDone={(code) => setVerificationCode(code)}
            />
            <Button
              disabled={isSubmitting || !canResend}
              onClick={() => sendCode(true)}
              variant={"text"}
              sx={{ fontWeight: "medium" }}
              color={"primary"}
            >
              {submitTitle}
            </Button>
          </Stack>
        </Stack>
      </CardWithIcon>
    </Minimal>
  );
};

export default MultiFactor;
